import { ArrowRight, ExternalLink, Mail, MessageCircle, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useSession } from '../../../app/useSession';
import { useTeamMember } from '../../../app/useTeamMember';
import { EntityIdBadge } from '../../../components/EntityIdBadge';
import { cn } from '../../../lib/cn';
import { mailtoHref, telHref, whatsappHref } from '../../../lib/quickActions';
import { formatHebrewRelative } from '../../../lib/relativeTime';
import { ConversationTimeline } from '../../bot/components/ConversationTimeline';
import { LeadLifecycleCard } from '../../bot/components/LeadLifecycleCard';
import { useBotConversationByLeadId } from '../../bot/botQueries';
import {
  LIFECYCLE_LEAD_STATUS_LABELS_HE,
  type LifecycleLeadStatus,
} from '../../bot/bot.schema';
import { useLead, useLeadActivity, type LeadActivityRow } from './leadsQueries';
import styles from './LeadProfile.module.css';

const STATUS_CLASS: Record<LifecycleLeadStatus, string | undefined> = {
  new: styles.statusNew,
  contacted: styles.statusContacted,
  qualified: styles.statusQualified,
  quoted: styles.statusQuoted,
  converted: styles.statusConverted,
  disqualified: styles.statusDisqualified,
};

const ACTION_LABELS: Record<string, string> = {
  'leads.insert': 'נוצר',
  'leads.update': 'עודכן',
  'leads.delete': 'נמחק',
  'lead.status_changed': 'שינוי סטטוס',
  'lead.note_appended': 'הערה נוספה',
  'lead.converted_to_customer': 'הומר ללקוח',
  'lead.quote_created': 'נוצרה הצעת מחיר',
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function Empty() {
  return <span className={cn(styles.fieldValue, styles.fieldEmpty)}>—</span>;
}

export function LeadProfile() {
  const { id } = useParams<{ id: string }>();
  const session = useSession();
  const sessionObj = session.status === 'signed-in' ? session.session : null;
  const { data: teamMember } = useTeamMember(sessionObj);
  const orgId = teamMember?.organization_id ?? null;

  const { data: lead, isLoading } = useLead(orgId, id ?? null);
  const { data: conversation } = useBotConversationByLeadId(orgId, id ?? null);
  const { data: activity = [] } = useLeadActivity(orgId, id ?? null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>טוען…</p>
      </div>
    );
  }
  if (!lead) {
    return (
      <div className={styles.page}>
        <p className={styles.empty}>הליד לא נמצא.</p>
        <Link to="/leads" className={styles.backLink}>
          <ArrowRight size={14} aria-hidden="true" /> חזרה ללידים
        </Link>
      </div>
    );
  }

  const wa = whatsappHref(lead.phone);
  const tel = telHref(lead.phone);
  const mailto = mailtoHref(lead.email);
  const statusKey = lead.status as LifecycleLeadStatus;
  const statusClass = STATUS_CLASS[statusKey] ?? styles.statusNew;

  return (
    <div className={styles.page}>
      <Link to="/leads" className={styles.backLink}>
        <ArrowRight size={14} aria-hidden="true" /> חזרה ללידים
      </Link>

      <header className={styles.header}>
        <div className={styles.headerTopRow}>
          <div className={styles.headerTitle}>
            <h1 className={styles.headerName}>{lead.full_name}</h1>
            <div className={styles.headerSub}>
              {lead.company_name ? <span>{lead.company_name}</span> : null}
              {lead.phone ? (
                <span>· <bdi dir="ltr">{lead.phone}</bdi></span>
              ) : null}
              {lead.email ? (
                <span>· <bdi dir="ltr">{lead.email}</bdi></span>
              ) : null}
              <span>· נפתח {formatHebrewRelative(lead.received_at)}</span>
            </div>
            <div style={{ marginBlockStart: 'var(--space-1)' }}>
              <EntityIdBadge id={lead.id} />
            </div>
          </div>
          <div className={styles.headerActions}>
            <span className={cn(styles.statusBadge, statusClass)}>
              {LIFECYCLE_LEAD_STATUS_LABELS_HE[statusKey]}
            </span>
            {wa ? (
              <a href={wa} target="_blank" rel="noopener noreferrer" className={styles.quickAction}>
                <MessageCircle size={14} aria-hidden="true" /> WhatsApp
              </a>
            ) : null}
            {tel ? (
              <a href={tel} className={styles.quickAction}>
                <Phone size={14} aria-hidden="true" /> חיוג
              </a>
            ) : null}
            {mailto ? (
              <a href={mailto} className={styles.quickAction}>
                <Mail size={14} aria-hidden="true" /> מייל
              </a>
            ) : null}
            {conversation ? (
              <Link to={`/bot/conversations/${conversation.id}`} className={styles.quickAction}>
                <ExternalLink size={14} aria-hidden="true" /> שיחת בוט
              </Link>
            ) : null}
          </div>
        </div>
      </header>

      <div className={styles.twoCol}>
        <div className={styles.col}>
          {conversation ? (
            <ConversationTimeline orgId={orgId} conversationId={conversation.id} />
          ) : (
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>שיחת בוט</h3>
              <p className={styles.empty}>הליד הזה לא הגיע דרך WhatsApp.</p>
            </div>
          )}

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>פרטי הליד</h3>
            <dl className={styles.fields}>
              <dt className={styles.fieldLabel}>שם</dt>
              <dd className={styles.fieldValue}>{lead.full_name}</dd>

              <dt className={styles.fieldLabel}>חברה</dt>
              <dd className={styles.fieldValue}>{lead.company_name ?? <Empty />}</dd>

              <dt className={styles.fieldLabel}>טלפון</dt>
              <dd className={styles.fieldValue}>
                {lead.phone ? <bdi dir="ltr">{lead.phone}</bdi> : <Empty />}
              </dd>

              <dt className={styles.fieldLabel}>מייל</dt>
              <dd className={styles.fieldValue}>
                {lead.email ? <bdi dir="ltr">{lead.email}</bdi> : <Empty />}
              </dd>

              <dt className={styles.fieldLabel}>עניין</dt>
              <dd className={styles.fieldValue}>{lead.interest ?? <Empty />}</dd>

              <dt className={styles.fieldLabel}>דירוג</dt>
              <dd className={styles.fieldValue}>
                {lead.score !== null ? <span className="num">{String(lead.score)}</span> : <Empty />}
              </dd>

              <dt className={styles.fieldLabel}>עודכן</dt>
              <dd className={styles.fieldValue}>{formatHebrewRelative(lead.updated_at)}</dd>
            </dl>
          </div>

          {lead.notes ? (
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>הערות</h3>
              <div className={styles.notesBlock}>{lead.notes}</div>
            </div>
          ) : null}
        </div>

        <div className={styles.col}>
          <LeadLifecycleCard orgId={orgId} leadId={lead.id} />

          <div className={styles.card}>
            <h3 className={styles.cardTitle}>פעילות</h3>
            {activity.length === 0 ? (
              <p className={styles.empty}>אין פעילות עדיין.</p>
            ) : (
              <div className={styles.activityList}>
                {activity.slice(0, 12).map((row: LeadActivityRow) => (
                  <div key={row.id} className={styles.activityItem}>
                    <span className={styles.activityAction}>{actionLabel(row.action)}</span>
                    <span className={styles.activityTime}>{formatHebrewRelative(row.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
